import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Clock,
  Phone,
  MapPin,
  Shield,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Send,
  Mail,
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { SEO } from '../components/shared/SEO';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { COMPANY_INFO, SERVICES, SERVICE_AREAS } from '../data/mock';
import {
  bookingUuidFor,
  clearPendingBooking,
  isRetryable,
  isVerifiedBooking,
  sendConversionOnce,
  RETRY_MESSAGE,
} from '../lib/bookingRequest';

const MAPS_API_KEY = 'AIzaSyCyc0IIoR5rywlY7Ltf_9D7xnJ_6KLnIGo';

// Minimum date = tomorrow (same-day scheduling not allowed)
const getTodayString = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

// Canonical customer-facing time vocabulary — matches Quotation.custom_scheduled_time.
// "All Day" is an admin/staff-only value (set by dispatch for a genuine full-day job)
// and must never be offered to customers here — flexible customers get "Any Time".
const ERP_SLOTS = [
  { value: 'Any Time',  label: 'Any Time (Flexible)' },
  { value: '8AM-11AM',  label: '8 AM – 11 AM' },
  { value: '11AM-2PM',  label: '11 AM – 2 PM' },
  { value: '2PM-5PM',   label: '2 PM – 5 PM' },
  { value: 'Emergency', label: 'Emergency / ASAP' },
];

// Canonical booking endpoint (portal backend) — creates Lead + Quotation so the
// booking reaches Google Calendar. The marketing site's own tech-backend
// /api/schedule (Lead-only, no Quotation, no calendar path) is deprecated for
// this form as of 2026-07-20 — see scheduler-memory/marketing_site_repoint_2026_07_20.md.
const SCHEDULE_ENDPOINT = 'https://portal.aaairrigationservice.com/api/schedule-appointment';

const FRISCO_ZIPS = new Set(['75033', '75034', '75035', '75036']);
const isFrisco = (address) => {
  if (!address) return false;
  if (/\bfrisco\b/i.test(address)) return true;
  const zip = (address.match(/\b(\d{5})\b/) || [])[1];
  return zip ? FRISCO_ZIPS.has(zip) : false;
};

// Extract city/state/zip from Google Places address_components — more
// reliable than splitting the backend's comma-formatted address string, and
// works even for addresses whose formatting doesn't match the usual
// "Street, City, ST ZIP" shape.
const parseAddressComponents = (components) => {
  const out = { city: '', state: '', zip: '' };
  if (!Array.isArray(components)) return out;
  for (const c of components) {
    const types = c.types || [];
    if (types.includes('locality')) out.city = c.long_name;
    else if (!out.city && types.includes('sublocality')) out.city = c.long_name;
    if (types.includes('administrative_area_level_1')) out.state = c.short_name;
    if (types.includes('postal_code')) out.zip = c.long_name;
  }
  return out;
};

export default function SchedulePage() {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    name: '',
    phone: '',
    email: '',
    service: '',
    address: '',
    // Populated from Google Places address_components when the user picks a
    // suggestion — lets the backend skip its brittle comma-split parsing of
    // `address` (which drops city/state/zip whenever a customer types the
    // street manually instead of selecting a suggestion, e.g. mobile Safari
    // where tapping a suggestion doesn't always fire place_changed reliably —
    // see scheduler-memory/abbey_matatall_missing_city_zip_2026_08_03.md).
    address_city: '',
    address_state: '',
    address_zip: '',
    preferred_date: '',
    preferred_time: '',
    notes: '',
    website: '', // honeypot
    sms_consent: false,
  });

  // True once the address was picked from a real Google Places suggestion
  // (so we know address_city/state/zip are trustworthy) — false if the user
  // is just typing free text.
  const [addressSelected, setAddressSelected] = useState(false);

  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: null,
  });

  // Store the confirmed booking details for the success card
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const bookingMemory = useRef({ pending: null });
  const submittingRef = useRef(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  // Slot availability from calendar
  const [availability, setAvailability] = useState(null); // null = not fetched yet
  const [availLoading, setAvailLoading] = useState(false);
  const [availNotice, setAvailNotice] = useState('');

  const addressRef = useRef(null);
  const autocompleteRef = useRef(null);

  // ── Google Places Autocomplete ──────────────────────────────────────────
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.onload = initAutocomplete;
      document.head.appendChild(script);
    } else {
      initAutocomplete();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const initAutocomplete = () => {
    if (!addressRef.current || !window.google) return;
    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      addressRef.current,
      {
        componentRestrictions: { country: 'us' },
        fields: ['formatted_address', 'address_components'],
        types: ['address'],
      }
    );
    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      if (place.formatted_address) {
        const parts = parseAddressComponents(place.address_components);
        setFormData((prev) => ({
          ...prev,
          address: place.formatted_address,
          address_city: parts.city,
          address_state: parts.state,
          address_zip: parts.zip,
        }));
        setAddressSelected(true);
      }
    });
  };

  // Fetch slot availability whenever date or address changes
  useEffect(() => {
    const date = formData.preferred_date;
    if (!date) { setAvailability(null); setAvailNotice(''); return; }
    // Block weekends client-side immediately
    const dow = new Date(date + 'T12:00:00').getDay(); // use noon to avoid timezone shifts
    if (dow === 0 || dow === 6) {
      setAvailability(new Set());
      setAvailNotice('We don\'t schedule appointments on weekends. Please choose a weekday.');
      setAvailLoading(false);
      setFormData(prev => ({ ...prev, preferred_time: '' }));
      return;
    }
    setAvailLoading(true);
    const params = new URLSearchParams({ date });
    if (formData.address) params.set('address', formData.address);
    fetch(`/api/availability?${params}`)
      .then(r => r.json())
      .then(data => {
        // Build a Set of available slot values for quick lookup
        const avail = new Set((data.slots || []).filter(s => s.available).map(s => s.slot));
        setAvailability(avail);
        setAvailNotice(data.notice || '');
        // Clear selected time if it's no longer available (Emergency/Any Time are
        // never slot-checked — they're not tied to a specific calendar window)
        if (formData.preferred_time && !avail.has(formData.preferred_time) && formData.preferred_time !== 'Emergency' && formData.preferred_time !== 'Any Time') {
          setFormData(prev => ({ ...prev, preferred_time: '' }));
        }
      })
      .catch(() => setAvailability(null))
      .finally(() => setAvailLoading(false));
  }, [formData.preferred_date, formData.address]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'address') {
      // Any manual edit invalidates a previously-selected suggestion — the
      // city/state/zip we captured no longer necessarily match the new text.
      setAddressSelected(false);
      setFormData((prev) => ({ ...prev, address: value, address_city: '', address_state: '', address_zip: '' }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setStatus({ loading: false, success: false, error: 'Please enter both your first and last name.' });
      return;
    }

    // Hard block weekends before showing modal
    if (formData.preferred_date) {
      const dow = new Date(formData.preferred_date + 'T12:00:00').getDay();
      if (dow === 0 || dow === 6) {
        setStatus({ loading: false, success: false, error: 'We don\'t schedule appointments on weekends. Please choose a weekday.' });
        return;
      }
    }

    setShowDisclaimer(true);
  };

  const handleConfirm = async () => {
    setShowDisclaimer(false);

    // Double-check weekend — no path around it
    if (formData.preferred_date) {
      const dow = new Date(formData.preferred_date + 'T12:00:00').getDay();
      if (dow === 0 || dow === 6) {
        setStatus({ loading: false, success: false, error: "We don't schedule appointments on weekends. Please choose a weekday." });
        return;
      }
    }

    if (submittingRef.current) return;
    submittingRef.current = true;
    setStatus({ loading: true, success: false, error: null });

    const fullName = `${formData.first_name.trim()} ${formData.last_name.trim()}`;
    const payload = {
      ...formData,
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      name: fullName,
      source: 'Website',
    };
    // Same details as a failed/unanswered attempt -> same booking_uuid (the
    // backend returns that booking instead of creating a second one); any
    // change -> a new one. See src/lib/bookingRequest.js.
    const bookingUuid = bookingUuidFor(payload, bookingMemory.current);

    try {
      // Canonical endpoint (portal backend) — always creates Lead + Quotation
      // so the booking reaches Google Calendar. source: "Website" tags the
      // internal note "[Via Website — Schedule Form]".
      let response;
      try {
        response = await fetch(SCHEDULE_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, booking_uuid: bookingUuid }),
        });
      } catch (networkError) {
        throw new Error(RETRY_MESSAGE);
      }
      const data = await response.json().catch(() => null);

      if (response.status === 429) {
        throw new Error((data && typeof data.detail === 'string' && data.detail) || 'Too many requests. Please try again later or call (469) 751-3567.');
      }
      if (!response.ok) {
        if (isRetryable(response.status, data)) throw new Error(RETRY_MESSAGE);
        throw new Error((data && typeof data.detail === 'string' && data.detail) || 'Failed to submit. Please try again or call us directly.');
      }
      if (!data || data.success !== true) {
        throw new Error((data && data.message) || 'Something went wrong. Please call us at (469) 751-3567.');
      }

      clearPendingBooking(bookingMemory.current);
      // Count the Google Ads conversion only for a booking ERP confirmed it
      // stored (Lead/Quotation ids for the UUID we sent), once per booking.
      if (isVerifiedBooking(data, bookingUuid)) {
        sendConversionOnce(bookingUuid);
      }
      setConfirmedBooking({ ...payload });
      setStatus({ loading: false, success: true, error: null });
    } catch (error) {
      setStatus({ loading: false, success: false, error: error.message });
    } finally {
      submittingRef.current = false;
    }
  };

  // ── Success card ────────────────────────────────────────────────────────
  if (status.success && confirmedBooking) {
    const timeLabel = ERP_SLOTS.find((t) => t.value === confirmedBooking.preferred_time)?.label || confirmedBooking.preferred_time;
    return (
      <Layout>
        {/* Hero */}
        <section className="bg-gradient-to-b from-slate-900 to-slate-800 py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Appointment Requested</h1>
            <p className="text-lg text-slate-300">We'll confirm your booking within 24 hours.</p>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-slate-50">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="border-teal-200 bg-teal-50">
              <CardContent className="pt-6">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-teal-600" />
                  </div>
                  <h3 className="text-2xl font-semibold text-slate-900 mb-2">Booking Request Received!</h3>
                  <p className="text-slate-600">
                    We received your request and will call or text to confirm your appointment. Your date and time window are a request until we confirm them.
                  </p>
                </div>

                {/* Booking summary */}
                <div className="bg-white rounded-xl border border-teal-100 p-6 space-y-3 text-sm mb-6">
                  <h4 className="font-semibold text-slate-800 text-base mb-4">Your Request</h4>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-slate-700">
                    <span className="font-medium">Name</span>
                    <span>{confirmedBooking.name}</span>
                    <span className="font-medium">Phone</span>
                    <span>{confirmedBooking.phone}</span>
                    <span className="font-medium">Email</span>
                    <span>{confirmedBooking.email}</span>
                    <span className="font-medium">Service</span>
                    <span>{confirmedBooking.service || 'Not specified'}</span>
                    <span className="font-medium">Address</span>
                    <span>{confirmedBooking.address || 'Not provided'}</span>
                    <span className="font-medium">Requested date</span>
                    <span>{confirmedBooking.preferred_date}</span>
                    <span className="font-medium">Requested time</span>
                    <span>{timeLabel}</span>
                    {confirmedBooking.notes && (
                      <>
                        <span className="font-medium">Notes</span>
                        <span>{confirmedBooking.notes}</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                  <p className="text-amber-800 font-medium mb-1">Need immediate service?</p>
                  <a
                    href={`tel:${COMPANY_INFO.phone}`}
                    className="text-xl font-bold text-teal-600 hover:text-teal-700"
                  >
                    Call / Text: {COMPANY_INFO.phoneFormatted}
                  </a>
                </div>

                <p className="text-xs text-center text-slate-400 mt-6">Powered by Boatman Systems™</p>
              </CardContent>
            </Card>
          </div>
        </section>
      </Layout>
    );
  }

  // ── Booking form ────────────────────────────────────────────────────────
  return (
    <Layout>
      <SEO
        title="Schedule Sprinkler Repair Online | AAA Irrigation | Allen, TX"
        description="Book sprinkler &amp; irrigation repair online in Allen, Plano, McKinney, Frisco TX. Emergency service available. TCEQ Licensed. Call (469) 751-3567."
        canonical="https://aaairrigationservice.com/schedule"
        keywords="schedule sprinkler repair McKinney TX, book irrigation service Plano, online appointment Allen TX irrigation, sprinkler repair Frisco, irrigation appointment Collin County"
      />
      {/* Hero */}
      <section className="bg-gradient-to-b from-slate-900 to-slate-800 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <a
              href={COMPANY_INFO.licenseUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-500/20 rounded-full px-4 py-1.5 mb-6 hover:bg-teal-500/20 transition-colors"
            >
              <Shield className="w-4 h-4 text-teal-400" />
              <span className="text-sm text-teal-400 font-medium">
                TCEQ Licensed: {COMPANY_INFO.license}
              </span>
            </a>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Schedule an Appointment
            </h1>
            <p className="text-lg text-slate-300">
              Book your irrigation service online — we'll confirm within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Form + Sidebar */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

            {/* Booking Card */}
            <div className="lg:col-span-2">
              <Card className="border-slate-200 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl text-slate-900">Book Your Service</CardTitle>
                  <CardDescription>
                    Fill out the form below and we'll confirm your appointment soon.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Honeypot — hidden from real users */}
                    <div className="hidden" aria-hidden="true">
                      <input
                        type="text"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    {/* Row 1: First Name + Last Name */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="first_name" className="block text-sm font-medium text-slate-700 mb-1">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text" id="first_name" name="first_name" required
                          minLength={2} maxLength={50}
                          value={formData.first_name} onChange={handleChange}
                          autoComplete="given-name"
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                          placeholder="John"
                        />
                      </div>
                      <div>
                        <label htmlFor="last_name" className="block text-sm font-medium text-slate-700 mb-1">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text" id="last_name" name="last_name" required
                          minLength={2} maxLength={50}
                          value={formData.last_name} onChange={handleChange}
                          autoComplete="family-name"
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                          placeholder="Doe"
                        />
                      </div>
                    </div>

                    {/* Row 2: Phone + Email */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1">
                          Phone <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel" id="phone" name="phone" required
                          maxLength={20}
                          value={formData.phone} onChange={handleChange}
                          autoComplete="tel"
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                          placeholder="(469) 555-1234"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email" id="email" name="email" required
                          value={formData.email} onChange={handleChange}
                          autoComplete="email"
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    {/* Row 3: Service */}
                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-slate-700 mb-1">
                        Service Needed <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="service" name="service" required
                        value={formData.service} onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors bg-white"
                      >
                        <option value="">Select a service...</option>
                        {SERVICES.map((s) => (
                          <option key={s.id} value={s.title}>{s.title}</option>
                        ))}
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {/* Service Address */}
                    <div>
                      <label htmlFor="address" className="block text-sm font-medium text-slate-700 mb-1">
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-teal-500" />
                          Service Address <span className="text-red-500">*</span>
                        </span>
                      </label>
                      <input
                        ref={addressRef}
                        type="text" id="address" name="address" required
                        value={formData.address} onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                        placeholder="Start typing your address..."
                        autoComplete="off"
                      />
                      {formData.address && !addressSelected && (
                        <p className="mt-1 text-xs text-amber-600">
                          Please pick your address from the dropdown suggestions so we can confirm your city and ZIP code.
                        </p>
                      )}
                    </div>

                    {/* Row 3: Preferred Date + Time */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="preferred_date" className="block text-sm font-medium text-slate-700 mb-1">
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-teal-500" />
                            Preferred Date <span className="text-red-500">*</span>
                          </span>
                        </label>
                        <input
                          type="date" id="preferred_date" name="preferred_date" required
                          min={getTodayString()}
                          value={formData.preferred_date} onChange={handleChange}
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                        />
                      </div>
                      <div>
                        <label htmlFor="preferred_time" className="block text-sm font-medium text-slate-700 mb-1">
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-4 h-4 text-teal-500" />
                            Preferred Time <span className="text-red-500">*</span>
                            {availLoading && <Loader2 className="w-3 h-3 animate-spin text-slate-400 ml-1" />}
                          </span>
                        </label>
                        <select
                          id="preferred_time" name="preferred_time" required
                          value={formData.preferred_time} onChange={handleChange}
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors bg-white"
                        >
                          <option value="">Select a time window...</option>
                          {ERP_SLOTS.map((t) => {
                            const isWeekend = availability && availability.size === 0 && availNotice.includes('weekend');
                            const booked = isWeekend || (availability && !availability.has(t.value) && t.value !== 'Emergency' && t.value !== 'Any Time');
                            return (
                              <option key={t.value} value={t.value} disabled={booked}>
                                {t.label}{booked ? ' — Unavailable' : ''}
                              </option>
                            );
                          })}
                        </select>
                        {availNotice && (
                          <p className="text-amber-600 text-xs mt-1">{availNotice}</p>
                        )}
                        <p className="text-slate-500 text-xs mt-1">This is your preferred window. We'll call or text to confirm your arrival time.</p>
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <label htmlFor="notes" className="block text-sm font-medium text-slate-700 mb-1">
                        Additional Notes
                        <span className="text-slate-400 font-normal"> (optional)</span>
                      </label>
                      <textarea
                        id="notes" name="notes" rows={4} maxLength={2000}
                        value={formData.notes} onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors resize-none"
                        placeholder="Describe your issue, gate codes, access instructions, or anything else we should know..."
                      />
                    </div>

                    {/* SMS Consent */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="sms_consent"
                          checked={formData.sms_consent}
                          onChange={handleChange}
                          className="mt-0.5 w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 flex-shrink-0"
                        />
                        <span className="text-xs text-slate-600 leading-relaxed">
                          I consent to receive appointment reminders and service updates via text message from{' '}
                          <strong>AAA Irrigation Service</strong>. Msg &amp; data rates may apply. Msg frequency varies.
                          Reply <strong>STOP</strong> to opt out or <strong>HELP</strong> for support at any time.{' '}
                          <a href="/privacy" className="text-teal-600 hover:underline">Privacy Policy</a>
                          {' '}&amp;{' '}
                          <a href="/terms" className="text-teal-600 hover:underline">Terms</a>.
                        </span>
                      </label>
                    </div>

                    {/* Error */}
                    {status.error && (
                      <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700">
                        <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Error</p>
                          <p className="text-sm">{status.error}</p>
                        </div>
                      </div>
                    )}

                    <Button
                      type="submit" disabled={status.loading}
                      className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3 h-auto text-base shadow-lg shadow-teal-600/20 hover:shadow-teal-600/40 transition-all duration-300"
                    >
                      {status.loading ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Submitting...</>
                      ) : (
                        <><Send className="w-5 h-5 mr-2" />Request Appointment</>
                      )}
                    </Button>

                    {/* Contact fallback */}
                    <div className="text-center pt-4 border-t border-slate-200">
                      <p className="text-sm text-slate-500 mb-2">Prefer to call?</p>
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <a
                          href={`tel:${COMPANY_INFO.phone}`}
                          className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium"
                        >
                          <Phone className="w-4 h-4" />{COMPANY_INFO.phoneFormatted}
                        </a>
                        <a
                          href={`mailto:${COMPANY_INFO.email}`}
                          className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium"
                        >
                          <Mail className="w-4 h-4" />Email Us
                        </a>
                      </div>
                    </div>

                    <p className="text-xs text-center text-slate-400 pt-2">
                      Powered by Boatman Systems™
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick call card */}
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Need It Fast?</h3>
                <p className="text-sm text-slate-600 mb-4">
                  For emergency service, call or text us directly.
                </p>
                <a
                  href={`tel:${COMPANY_INFO.phone}`}
                  className="flex items-center gap-3 text-slate-700 hover:text-teal-600 transition-colors"
                >
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-semibold">{COMPANY_INFO.phoneFormatted}</p>
                    <p className="text-sm text-slate-500">Call or Text</p>
                  </div>
                </a>
              </div>

              {/* Hours card */}
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-teal-500" />
                  Business Hours
                </h3>
                <div className="space-y-2 text-slate-600 text-sm">
                  <p>{COMPANY_INFO.hours.weekday}</p>
                  <p>{COMPANY_INFO.hours.saturday}</p>
                  <p className="text-amber-600 font-medium">{COMPANY_INFO.hours.sunday}</p>
                </div>
              </div>

              {/* Service areas */}
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-teal-500" />
                  Service Areas
                </h3>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_AREAS.map((area) => (
                    <Link
                      key={area}
                      to={`/locations/${area.toLowerCase()}`}
                      className="inline-block bg-slate-100 hover:bg-teal-50 text-slate-700 hover:text-teal-700 text-sm px-3 py-1 rounded-full transition-colors"
                    >
                      {area}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Trust badge */}
              <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl p-6 text-white shadow-sm">
                <Shield className="w-8 h-8 mb-3 text-teal-200" />
                <h3 className="font-semibold text-lg mb-1">Licensed &amp; Insured</h3>
                <p className="text-teal-100 text-sm">
                  TCEQ License {COMPANY_INFO.license}. Fully insured for your peace of mind.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer Modal */}
      {showDisclaimer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-3">Before We Confirm Your Request</h3>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 text-sm text-slate-700 space-y-3">
              <p className="font-semibold text-slate-800">
                Inspection / System Checkup — <span className="text-teal-700">{isFrisco(formData.address) ? '$125' : '$95'}</span>
              </p>
              <p className="text-amber-800 text-xs font-medium">The inspection/system checkup is a required first step before any repair work can be authorized.</p>
              <ul className="space-y-1.5 text-slate-700">
                <li className="flex items-start gap-2"><span className="text-teal-600 font-bold mt-0.5">✓</span><span>Complete and thorough inspection of the entire system</span></li>
                <li className="flex items-start gap-2"><span className="text-teal-600 font-bold mt-0.5">✓</span><span>Minor adjustments at no extra charge (e.g. nozzle and spray pattern adjustments)</span></li>
                <li className="flex items-start gap-2"><span className="text-teal-600 font-bold mt-0.5">✓</span><span>Full repair quote provided after inspection — work only begins with your approval</span></li>
              </ul>
              <p className="text-slate-600 border-t border-amber-200 pt-2">
                You are never obligated to proceed beyond the inspection.
              </p>
              {isFrisco(formData.address) && (
                <p className="text-amber-800 font-medium">
                  Frisco, TX extended service area — fee is <strong>$125</strong>.
                </p>
              )}
              <p className="text-slate-500 text-xs">Fee is due at time of service.</p>
            </div>
            <p className="text-sm text-slate-500 mb-5">
              By clicking <strong>I Understand — Submit Request</strong>, you acknowledge the inspection fee and agree to be contacted by AAA Irrigation Service.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDisclaimer(false)}
                className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 font-medium transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors"
              >
                I Understand — Submit Request
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
